import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import React, { useEffect, useMemo, useState } from "react";
import { MuiBox } from "../box/MuiBox";
import { MuiIconButton } from "../button/MuiButton";
import MuiChip, { MuiChipProps } from "../chip/MuiChip";
import MuiEllipseLabel from "../ellipseLabel/MuiEllipseLabel";
import { MuiTypography } from "../typography/MuiTypography";
import { BaseStyledChipPopover } from "./MuiChipWithPopoverBase.style";

export interface MuiChipWithPopoverBaseProps extends MuiChipProps {
  gridRef: React.RefObject<HTMLElement>;
  headerLabel: string;
  isDialogTitleWithEllipsis?: boolean;
  minPopoverWidth?: number;
  showDoubleLabel?: boolean; // Flag to control double label rendering
  closePopoverARIALabel: string;
  chipDoubleLabelPattern?: string; // Pattern for double label rendering
  children?: React.ReactNode;
}

export const MuiChipWithPopoverBase: React.FC<MuiChipWithPopoverBaseProps> = ({
  id = "",
  data,
  gridRef,
  headerLabel,
  countToWrap,
  isEditable = false,
  minPopoverWidth = 320,
  closePopoverARIALabel,
  isDialogTitleWithEllipsis = false,
  removeChipNameARIALabel,
  showMoreARIALabel,
  showDoubleLabel = false,
  chipDoubleLabelPattern = "",
  infoMessage,
  children,
}) => {
  // Store the width of the grid
  const [gridWidth, setGridWidth] = useState(0);
  const [anchorElChip, setAnchorElChip] = useState<null | HTMLElement>(null);

  useEffect(() => {
    // Function to measure the width of the grid
    if (gridRef.current) {
      setGridWidth(gridRef.current.getBoundingClientRect().width);
    }
  }, [gridRef.current]);

  const handleChipOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElChip(event.currentTarget);
  };

  const handleCloseIconClick = () => {
    setAnchorElChip(null);
  };

  const handleChipClose = () => {
    setAnchorElChip(null);
  };

  const newData = useMemo(() => {
    if (showDoubleLabel && chipDoubleLabelPattern) {
      return data.map((item) => {
        if (item.label && item.additionalInfo) {
          return {
            ...item,
            label: chipDoubleLabelPattern
              .replace("label", item.label)
              .replace("additionalInfo", item.additionalInfo),
          };
        }
        return item;
      });
    }
    return data;
  }, [data]);

  return (
    <MuiBox id={id}>
      <MuiChip
        id={id}
        data={newData}
        handleExpand={handleChipOpen}
        countToWrap={countToWrap}
        isEditable={isEditable}
        removeChipNameARIALabel={removeChipNameARIALabel}
        showMoreARIALabel={showMoreARIALabel}
        infoMessage={infoMessage}
      />

      <BaseStyledChipPopover
        open={Boolean(anchorElChip)}
        anchorEl={anchorElChip}
        className="base-chip-popover"
        popoverWidth={gridWidth}
        minWidth={minPopoverWidth}
        onClose={handleChipClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
      >
        <MuiBox className="header-title">
          {isDialogTitleWithEllipsis ? (
            <MuiEllipseLabel
              text={headerLabel}
              labelTypoClassName={`ellipses-title max-width`}
              labelTypoVariant="h2SemiBold"
            />
          ) : (
            <MuiTypography
              component="h2"
              className="text-title"
              variant="h2SemiBold"
            >
              {headerLabel}
            </MuiTypography>
          )}
          <MuiIconButton
            aria-label={closePopoverARIALabel}
            onClick={handleCloseIconClick}
          >
            <CloseRoundedIcon className="close-icon" />
          </MuiIconButton>
        </MuiBox>
        {children}
      </BaseStyledChipPopover>
    </MuiBox>
  );
};
